import React, { useEffect, useState } from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'

import { foodsActions, Food } from '../../core/foods'
import { brandsActions, Brand, BRAND_CODES } from '../../core/brands'
import RootState from '../../core/root-state'
import Layout from '../layouts/layout'
import Wrapper from '../layouts/wrapper'
import { strings } from '../components/foods/foods-content'
import styles from '../styles/modules/foods/foods.module.scss'
import FoodTable from '../components/foods/food-table'
import { BrandFilter, BrandCode } from '../components/common'
import { roleType, Permission } from '../../core/auth0'
import withPermissionCheck from '../../core/access-control/with-permission-check'

const getDefaultBrandCodeForAdmin = (isAdmin: boolean): BrandCode => {
  return isAdmin ? BRAND_CODES.ROOTC : null
}

const getInitialBrandCode = (
  shouldRestoreBrand: boolean,
  currentBrandCode: string | null,
  isAdmin: boolean
): BrandCode => {
  const defaultBrandCode = getDefaultBrandCodeForAdmin(isAdmin)
  return shouldRestoreBrand
    ? currentBrandCode ?? defaultBrandCode
    : defaultBrandCode
}

interface Props {
  foods: Food[]
  isLoading: boolean
  role: roleType
  brands: Brand[]
  currentBrandCode: string | null
  routerAction: string
  fetchFoods: (brandCode?: string | null) => void
  fetchFoodsEvery: () => void
  fetchFoodsEveryFinished: () => void
  changeFoodsIndex: (ids: number[]) => void
  fetchBrands: () => void
  setCurrentBrandCode: (brandCode: string | null) => void
}

const FoodsScreen: React.FC<Props> = ({
  foods,
  isLoading,
  role,
  brands,
  currentBrandCode,
  routerAction,
  fetchFoods,
  fetchFoodsEvery,
  fetchFoodsEveryFinished,
  changeFoodsIndex,
  fetchBrands,
  setCurrentBrandCode,
}) => {
  const isAdmin = role === roleType.ADMIN
  const shouldRestoreBrand = routerAction === 'POP'
  // adminユーザーは初期選択をrootcに設定し、rootcのデータを表示
  // Reduxに選択ブランドがある場合はそれを優先して復元する
  const initialBrandCode = getInitialBrandCode(
    shouldRestoreBrand,
    currentBrandCode,
    isAdmin
  )
  const [selectedBrand, setSelectedBrand] = useState<BrandCode>(initialBrandCode)

  useEffect(() => {
    setCurrentBrandCode(initialBrandCode)
    fetchFoods(initialBrandCode)
    fetchBrands()
    // 初期化処理のため、コンポーネントマウント時に一度だけ実行される必要がある
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchFoodsEvery()
    return () => {
      fetchFoodsEveryFinished()
    }
    // 継続取得処理の開始・終了制御のため、コンポーネントマウント時に一度だけ実行される必要がある
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleBrandChange = (brand: BrandCode) => {
    setSelectedBrand(brand)
  }

  const handleFilterClick = () => {
    setCurrentBrandCode(selectedBrand)
    fetchFoods(selectedBrand)
  }

  return (
    <Layout location={'foods'}>
      <Wrapper title={strings.title}>
        <div className={styles.filterContainer}>
          <BrandFilter
            currentBrand={selectedBrand}
            onBrandChange={handleBrandChange}
            onFilterClick={handleFilterClick}
            isAdminOnly={isAdmin}
            brands={brands}
            isLoading={isLoading}
          />
        </div>
        <div className={styles.container}>
          <FoodTable foods={foods} changeFoodsIndex={changeFoodsIndex} />
        </div>
      </Wrapper>
    </Layout>
  )
}

const mapStateToProps = (state: RootState) => ({
  foods: state.foods.foods,
  isLoading: state.foods.isLoading,
  role: state.auth0.role,
  brands: state.brands.brands,
  currentBrandCode: state.foods.currentBrandCode,
  routerAction: state.router.action,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchFoods: (brandCode?: string | null) =>
    dispatch(foodsActions.fetchFoods(brandCode)),
  fetchFoodsEvery: () => dispatch(foodsActions.fetchFoodsEvery()),
  fetchFoodsEveryFinished: () =>
    dispatch(foodsActions.fetchFoodsEveryFinished()),
  changeFoodsIndex: (ids: number[]) =>
    dispatch(foodsActions.changeFoodsIndex(ids)),
  fetchBrands: () => dispatch(brandsActions.fetchBrands()),
  setCurrentBrandCode: (brandCode: string | null) =>
    dispatch(foodsActions.setCurrentBrandCode(brandCode)),
})

const ConnectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(FoodsScreen)

export default withPermissionCheck(ConnectedComponent, {
  requiredPermission: Permission.FOOD_READ,
  redirectTo: '/404',
})
